import { useState, useEffect } from 'react';
import {
  Select,
  SelectItem,
  Tabs,
  Tab,
  Card,
  CardBody,
} from '@nextui-org/react';
import { DateRangePicker } from 'rsuite';
import type { DateRange } from 'rsuite/DateRangePicker';
import 'rsuite/dist/rsuite.css';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  FiltersToggle,
  PageLayout,
  SectionTitle,
} from '../../../components/ui';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useSharingStore from '../../../store/useSharingStore';
import DispertionChart from '../../../components/settlements/comparative-sales/DispertionChart';
import CaliberEvolutionChart from '../../../components/settlements/comparative-sales/CaliberEvolutionChart';
import VarietyEvolutionChart from '../../../components/settlements/comparative-sales/VarietyEvolutionChart';
import FormatEvolutionChart from '../../../components/settlements/comparative-sales/FormatEvolutionChart';
import MarketEvolutionChart from '../../../components/settlements/comparative-sales/MarketEvolutionChart';
import ColorEvolutionChart from '../../../components/settlements/comparative-sales/ColorEvolutionChart';
import MultiSelectWithActions from '../../../components/common/MultiSelectWithActions';
import {
  SettlementMethodology,
  SettlementGroupBy,
  SettlementDateField,
} from '../../../types/settlements';
import { supabase } from 'common/src/supabase';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';

type ChartTab = 'caliber' | 'variety' | 'format' | 'market' | 'color';

const SettlementsComparativeSales = () => {
  const { t } = useTranslation('settlementsComparative');

  const CHART_TABS: Record<ChartTab, string> = {
    caliber: t('Por Calibre'),
    variety: t('Por Variedad'),
    format: t('Por Formato'),
    market: t('Por Mercado'),
    color: t('Por Color'),
  };
  const maxDateRange = 30;

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [selectedTab, setSelectedTab] = useState<ChartTab>('caliber');
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );

  const { session, userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { fetchExceptions, isRestricted, exceptions } = useSharingStore();

  // Access control state
  const [hasAccess, setHasAccess] = useState(true);

  // Check if user is demo
  const isDemo = !!userProfile?.exporter?.hasDemo;

  // Demo restrictions: January 2025 dates
  const getDemoDateRange = (): DateRange => [
    new Date('2025-01-01T12:00:00'),
    new Date('2025-01-31T12:00:00'),
  ];

  const getDefaultDateRange = (): DateRange =>
    isDemo
      ? getDemoDateRange()
      : [new Date('2024-12-01T12:00:00'), new Date('2024-12-30T12:00:00')];

  const [filters, setFilters] = useState<{
    dateField: SettlementDateField;
    methodology: SettlementMethodology;
    dateRange: DateRange | null;
    groupBy: SettlementGroupBy;
  }>({
    dateField: 'date_sold',
    methodology: 'by_volume',
    dateRange: getDefaultDateRange(),
    groupBy: 'week',
  });

  const [error, setError] = useState<string | null>(null);
  const [markets, setMarkets] = useState<
    { id: number; name: string; country: string }[]
  >([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  // Helper function to validate date range
  const validateDateRange = (dateRange: DateRange | null): boolean => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return true; // Allow empty ranges
    }

    const startDate = new Date(dateRange[0]);
    const endDate = new Date(dateRange[1]);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= maxDateRange;
  };

  // Handle date range change with validation
  const handleDateRangeChange = (value: DateRange | null) => {
    // Demo users cannot change date range
    if (isDemo) {
      setError(t('Los usuarios demo solo pueden ver datos de enero 2025.'));
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (!value || !value[0] || !value[1]) {
      handleFilterChange('dateRange', value);
      return;
    }

    const startDate = new Date(value[0]);
    const endDate = new Date(value[1]);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > maxDateRange) {
      // Auto-adjust to maxDateRange days from start date
      const adjustedEndDate = new Date(startDate);
      adjustedEndDate.setDate(startDate.getDate() + maxDateRange);

      const adjustedRange: DateRange = [startDate, adjustedEndDate];
      handleFilterChange('dateRange', adjustedRange);

      // Show warning message
      setError(
        t(
          'El rango de fechas ha sido limitado a {{days}} días máximo para optimizar el rendimiento.',
          { days: maxDateRange }
        )
      );
      setTimeout(() => setError(null), 3000);
    } else {
      handleFilterChange('dateRange', value);
      if (error && error.includes('rango de fechas')) {
        setError(null);
      }
    }
  };

  // Hardcoded filter options
  const filterOptions = {
    dateField: [
      { key: 'date_sold', label: t('Fecha de Venta') },
      { key: 'etd', label: t('ETD (Fecha de Salida)') },
      { key: 'eta', label: t('ETA (Fecha de Llegada)') },
    ],
    methodology: [
      { key: 'by_volume', label: t('Por Volumen') },
      { key: 'by_exporter', label: t('Por Exportadora') },
    ],
    groupBy: [
      { key: 'week', label: t('Semanal') },
      { key: 'day', label: t('Diaria') },
    ],
  };

  const handleFilterChange = (filterName: string, value: any) => {
    // Demo users cannot change date range or certain filters
    if (isDemo && filterName === 'dateRange') {
      setError(t('Los usuarios demo solo pueden ver datos de enero 2025.'));
      setTimeout(() => setError(null), 3000);
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const fetchMarkets = async () => {
    if (
      !session ||
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies
    ) {
      return;
    }

    // Only allow cherry species (id === 2)
    if (selectedSpecies.id !== 2) {
      return;
    }

    try {
      const { data: settlements, error } = await supabase
        .from('settlements')
        .select(
          `
          market_id,
          exporter_id,
          market:markets!settlements_market_id_fkey(id, name, country)
        `
        )
        .eq('exporter_id', userProfile.exporter_id)
        .eq('season', selectedSeason)
        .eq('species_id', selectedSpecies.id)
        .not('market_id', 'is', null);

      if (error) throw error;

      // Get unique markets with country
      const uniqueMarkets =
        settlements
          ?.filter((s: any) => s.market)
          .reduce(
            (
              acc: { id: number; name: string; country: string }[],
              curr: any
            ) => {
              if (!acc.find((m) => m.id === curr.market.id)) {
                acc.push({
                  id: curr.market.id,
                  name: curr.market.name,
                  country: curr.market.country,
                });
              }
              return acc;
            },
            [] as { id: number; name: string; country: string }[]
          )
          .sort(
            (
              a: { id: number; name: string; country: string },
              b: { id: number; name: string; country: string }
            ) => a.name.localeCompare(b.name)
          ) || [];

      setMarkets(uniqueMarkets);

      // Build countries list
      const uniqueCountries = Array.from(
        new Set(uniqueMarkets.map((m) => m.country))
      ).sort((a, b) => a.localeCompare(b));
      setCountries(uniqueCountries);

      // Default country: China if exists, else first available
      const defaultCountry =
        uniqueCountries.find((c) => c?.toLowerCase() === 'china') ||
        uniqueCountries[0] ||
        null;
      setSelectedCountry(defaultCountry);

      // Preselect all markets from default country
      const defaultMarketIds = uniqueMarkets
        .filter((m) => (defaultCountry ? m.country === defaultCountry : true))
        .map((m) => m.id.toString());
      setSelectedMarkets(defaultMarketIds);
    } catch (err) {
      console.error('Error fetching markets:', err);
    }
  };

  // Fetch markets when component mounts or dependencies change
  useEffect(() => {
    fetchMarkets();
  }, [selectedSeason, selectedSpecies]);

  // When country changes, select all markets of that country
  useEffect(() => {
    if (!selectedCountry) return;
    const ids = markets
      .filter((m) => m.country === selectedCountry)
      .map((m) => m.id.toString());
    if (ids.length > 0) {
      setSelectedMarkets(ids);
    }
  }, [selectedCountry, markets]);

  // Clear error when switching to cherry species
  useEffect(() => {
    if (selectedSpecies?.id === 2) {
      setError(null);
    }
  }, [selectedSpecies]);

  // Force demo date range for demo users
  useEffect(() => {
    if (isDemo) {
      const demoRange = getDemoDateRange();
      setFilters((prev) => ({
        ...prev,
        dateRange: demoRange,
      }));
    }
  }, [isDemo]);

  // Fetch sharing exceptions when user profile is available
  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchExceptions(userProfile.exporter_id);
    }
  }, [userProfile?.exporter_id, fetchExceptions]);

  // Check if access is restricted for current species/season
  useEffect(() => {
    if (!selectedSpecies || !userProfile || !selectedSeason) {
      console.log('🔓 Sales: Missing data for access check', {
        selectedSpecies,
        userProfile,
        selectedSeason,
      });
      setHasAccess(true);
      return;
    }

    const restricted = isRestricted(selectedSpecies.id, selectedSeason);
    console.log('🔐 Sales: Access check result', {
      speciesId: selectedSpecies.id,
      season: selectedSeason,
      restricted,
      hasAccess: !restricted,
    });
    setHasAccess(!restricted);
  }, [selectedSpecies, selectedSeason, userProfile, isRestricted, exceptions]);

  const renderEvolutionChart = () => {
    // Pass all the necessary props to each chart component
    const commonProps = {
      methodology: filters.methodology,
      groupBy: filters.groupBy,
      dateField: filters.dateField,
      dateRange: filters.dateRange,
      selectedMarkets,
      session,
      userProfile,
      selectedSpecies,
      selectedSeason,
      visualization,
    };

    switch (selectedTab) {
      case 'caliber':
        return <CaliberEvolutionChart {...commonProps} />;
      case 'variety':
        return <VarietyEvolutionChart {...commonProps} />;
      case 'format':
        return <FormatEvolutionChart {...commonProps} />;
      case 'market':
        return <MarketEvolutionChart {...commonProps} />;
      case 'color':
        return <ColorEvolutionChart {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            {/* Error message display */}
            {error && (
              <Alert variant='danger' className='mb-4'>
                {error}
              </Alert>
            )}

            <div className='relative z-[100]'>
              <p className='text-sm text-default-500 mb-1'>
                {t('Rango de Fechas')}{' '}
                {isDemo
                  ? t('(Demo: Solo Enero 2025)')
                  : t('(máximo {{days}} días)', { days: maxDateRange })}
              </p>
              <DateRangePicker
                value={filters.dateRange}
                onChange={handleDateRangeChange}
                showWeekNumbers
                showOneCalendar
                placeholder={
                  isDemo
                    ? t('Demo: Enero 2025')
                    : t('Seleccionar rango de fechas (máx. {{days}} días)', {
                        days: maxDateRange,
                      })
                }
                character=' - '
                format='dd-MM-yyyy'
                className='w-full'
                menuStyle={{ zIndex: 9999 }}
                disabled={isDemo || !hasAccess}
                shouldDisableDate={(date) => {
                  // Demo users: only allow January 2025
                  if (isDemo) {
                    const currentDate = new Date(date);
                    const year = currentDate.getFullYear();
                    const month = currentDate.getMonth();
                    return !(year === 2025 && month === 0); // January is month 0
                  }

                  // If there's already a start date selected, disable dates more than maxDateRange days away
                  if (
                    filters.dateRange &&
                    filters.dateRange[0] &&
                    !filters.dateRange[1]
                  ) {
                    const startDate = new Date(filters.dateRange[0]);
                    const currentDate = new Date(date);
                    const diffTime = Math.abs(
                      currentDate.getTime() - startDate.getTime()
                    );
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    return diffDays > maxDateRange;
                  }
                  return false;
                }}
                locale={{
                  sunday: t('Do'),
                  monday: t('Lu'),
                  tuesday: t('Ma'),
                  wednesday: t('Mi'),
                  thursday: t('Ju'),
                  friday: t('Vi'),
                  saturday: t('Sa'),
                  ok: t('Aplicar'),
                  today: t('Hoy'),
                  yesterday: t('Ayer'),
                  last7Days: t('Últimos 7 días'),
                }}
              />
              {filters.dateRange &&
                !validateDateRange(filters.dateRange) &&
                !isDemo && (
                  <p className='text-xs text-red-500 mt-1'>
                    {t(
                      '⚠️ El rango seleccionado excede los {{days}} días máximos',
                      { days: maxDateRange }
                    )}
                  </p>
                )}
            </div>
            <p className='text-sm text-default-500 mb-1'>
              {t('Filtros y Agrupación')}
            </p>
            <Select
              label={t('Campo de Fecha')}
              value={filters.dateField}
              onChange={(e) => handleFilterChange('dateField', e.target.value)}
              defaultSelectedKeys={[filters.dateField]}
              className='w-full'
              isDisabled={!hasAccess}
            >
              {filterOptions.dateField.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Ponderación')}
              value={filters.methodology}
              onChange={(e) =>
                handleFilterChange('methodology', e.target.value)
              }
              defaultSelectedKeys={[filters.methodology]}
              className='w-full'
              isDisabled={!hasAccess}
            >
              {filterOptions.methodology.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Agrupación')}
              value={filters.groupBy}
              onChange={(e) => handleFilterChange('groupBy', e.target.value)}
              defaultSelectedKeys={[filters.groupBy]}
              className='w-full'
              isDisabled={!hasAccess}
            >
              {filterOptions.groupBy.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <Select
              label={t('Tipo de visualización')}
              value={visualization}
              onChange={(e) =>
                setVisualization(e.target.value as 'chart' | 'table')
              }
              defaultSelectedKeys={[visualization]}
              className='w-full'
              isDisabled={!hasAccess}
            >
              <SelectItem key='chart' value='chart' textValue={t('Gráficos')}>
                {t('Gráficos')}
              </SelectItem>
              <SelectItem key='table' value='table' textValue={t('Tablas')}>
                {t('Tablas')}
              </SelectItem>
            </Select>

            {markets.length > 0 && (
              <>
                <Select
                  label={t('País')}
                  selectedKeys={selectedCountry ? [selectedCountry] : []}
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0] as string;
                    setSelectedCountry(value);
                  }}
                  className='w-full'
                  size='sm'
                  isDisabled={!hasAccess}
                >
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </Select>
                <MultiSelectWithActions
                  label={t('Mercados de Venta')}
                  description={t(
                    'Al deseleccionar todos, se mantiene el primer mercado'
                  )}
                  placeholder={t('Selecciona mercados')}
                  options={markets
                    .filter((m) => m.country === selectedCountry)
                    .map((market) => ({
                      value: market.id.toString(),
                      label: market.name,
                    }))}
                  selectedKeys={selectedMarkets}
                  onSelectionChange={setSelectedMarkets}
                  className='w-full'
                  size='md'
                  isDisabled={!hasAccess}
                />
              </>
            )}
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout>
        <Header
          title={t('Serie de Precios')}
          description={t(
            'Análisis los precios de venta entre exportadoras que usan CherryMarket'
          )}
        />

        {isDemo && (
          <Alert variant='warning' className='mb-4'>
            <strong>{t('Cuenta Demo')}:</strong>{' '}
            {t(
              'Solo se muestran datos de enero 2025 con variedad Lapins para demostración.'
            )}
          </Alert>
        )}

        <Alert className='mb-4'>
          <strong>{t('Nota')}:</strong>{' '}
          {t(
            'Solo se muestran liquidaciones que tienen tax para asegurar comparaciones más precisas entre exportadoras.'
          )}
        </Alert>

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {selectedSpecies?.id !== 2 && (
          <Alert variant='danger' className='mb-4'>
            {t(
              'Esta visualización solo está disponible para cerezas. Por favor selecciona cereza en el filtro de especies.'
            )}
          </Alert>
        )}

        {!hasAccess && selectedSpecies?.id === 2 && (
          <Alert variant='warning' className='mb-4'>
            {t(
              'Tu organización no tiene permisos para acceder al módulo Industria para esta especie/temporada.'
            )}
          </Alert>
        )}

        {/* Dispersión Chart Section */}
        {selectedSpecies?.id === 2 && (
          <div
            className={`mb-8 ${!hasAccess ? 'blur-md pointer-events-none select-none' : ''}`}
          >
            <DispertionChart
              methodology={filters.methodology}
              groupBy={filters.groupBy}
              dateField={filters.dateField}
              dateRange={filters.dateRange}
              selectedMarkets={selectedMarkets}
              visualization={visualization}
            />
          </div>
        )}

        {/* Evolution Charts Section */}
        {selectedSpecies?.id === 2 && (
          <div
            className={`mb-8 ${!hasAccess ? 'blur-md pointer-events-none select-none' : ''}`}
          >
            <SectionTitle
              title={t('Evolución de Precios de Venta')}
              description={t(
                'Análisis de la evolución temporal de precios de venta por diferentes criterios'
              )}
            />

            <div className='overflow-x-auto'>
              <div className='min-w-[768px]'>
                <Tabs
                  aria-label={t('Opciones de gráficos de evolución')}
                  selectedKey={selectedTab}
                  onSelectionChange={(key) => setSelectedTab(key as ChartTab)}
                  className='mb-4'
                  size='lg'
                  variant='underlined'
                >
                  {Object.entries(CHART_TABS)
                    .filter(([key]) => {
                      if (key === 'color' && !selectedSpecies?.has_color) {
                        return false;
                      }
                      return true;
                    })
                    .map(([key, label]) => (
                      <Tab key={key} title={label} />
                    ))}
                </Tabs>
              </div>
            </div>

            <Card className='p-6'>
              <CardBody>{renderEvolutionChart()}</CardBody>
            </Card>
          </div>
        )}
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeSales;
